import { TinyAIPageSource } from '@repo/types';
import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Maps the current route to a TinyAIPageSource enum value.
 * Consolidates routes into broader categories for better tracking.
 *
 * @returns TinyAIPageSource enum value
 */
export const useTinyAIPageSource = (): TinyAIPageSource => {
  const { pathname } = useLocation();

  return useMemo(() => {
    // INSIGHTS - All metrics and metric detail pages
    if (pathname.match(/^\/results\/[^/]+\/metrics/)) {
      return TinyAIPageSource.INSIGHTS;
    }

    // RESULTS - All results-related pages (microbiome, conditions, seeding, surveys, consults)
    if (pathname.match(/^\/results\//)) {
      return TinyAIPageSource.RESULTS;
    }

    // ACTION_PLAN - Action plan pages
    if (pathname.match(/^\/action-plan\//)) {
      return TinyAIPageSource.ACTION_PLAN;
    }

    // ACCOUNT - Profile, account, subscriptions, membership
    if (pathname.match(/^\/(?:profile|account|membership)/)) {
      return TinyAIPageSource.ACCOUNT;
    }

    // HELP - Help, consults, guides
    if (pathname.match(/^\/(?:help|consults|guides)/)) {
      return TinyAIPageSource.HELP;
    }

    // COMMUNITY - Family, referrals, bookmarks, partner recommendations
    if (pathname.match(/^\/(?:family|referrals|bookmarks|partner-recs)/)) {
      return TinyAIPageSource.COMMUNITY;
    }

    // KITS - Kits, kit information, review, typeform
    if (pathname.match(/^\/(?:kits|information|review|typeform)/)) {
      return TinyAIPageSource.KITS;
    }

    // HOME
    if (pathname === '/') {
      return TinyAIPageSource.HOME;
    }

    // Default fallback
    return TinyAIPageSource.OTHER;
  }, [pathname]);
};
